import { AntDesign } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { router } from "expo-router";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, ScrollView, Pressable } from "react-native";
import { useDispatch } from "react-redux";

import Logo from "@/assets/logo.svg";
import Copyright from "@/components/Copyright";
import Loader from "@/components/Loader";
import ProgressBarComponent from "@/components/ProgressBarComponent";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import ButtonLogout from "@/components/button/ButtonLogout";
import Input from "@/components/input/Input";
import InputChoose from "@/components/input/InputChoose";
import InputDropList from "@/components/input/InputDropList";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { sendRequest } from "@/utils/ApiUtil";
import { CustomerDetailsCorporationObject } from "@/utils/dto/application.request";
import { CorporationCustomerCorporateEntityType } from "@/utils/dto/extracted_enums";

type FormData = {
  naics: string;
  naicsDescription: string;
  descriptionOfBusinessNature: string;
  corporateEntityType: string;
  corporateEntityTypeDescription: string;
};

interface NaicsData {
  code: string;
  description: string;
  return: {
    code: string;
    description: string;
  };
}

export default function Description() {
  const [searchTerm, setSearchTerm] = useState("");
  const [naics, setNaics] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [entityType, setEntityType] = useState("");

  const { t } = useTranslation();
  const states = useAppSelector((state: any) => state);
  const dispatch = useDispatch();

  const resolver = classValidatorResolver(CustomerDetailsCorporationObject, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, formState, setValue, setFocus } =
    useForm<FormData>({
      mode: "onSubmit",
      resolver,
    });

  const { isSubmitting } = formState;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/naics-code", {
          searchTerm,
        });
        const mappedNaics = response.map((naics: NaicsData) => ({
          value: naics.description,
          code: naics.code,
          uniqueKey: `${naics.description}-${naics.code}`,
        }));

        setNaics(mappedNaics);
      } catch (error: any) {
        console.error(error);
      }
    };

    if (searchTerm) {
      fetchData();
    }
  }, [searchTerm]);

  const onSubmit = async (data: FormData) => {
    const customerDetailsCorporation = {
      registeredName: states.registeredName,
      tradingName: states.tradingName,
      registeredNumber: states.registeredNumber,
      telephoneNumber: states.telephoneNumber,
      websiteAddress: states.websiteAddress,
      stateOfIncorporation: states.stateOfIncorporation,
      countryOfIncorporation: states.countryOfIncorporation,
      corporateEntityType: data.corporateEntityType,
      corporateEntityTypeDescription: data.corporateEntityTypeDescription,
      emailAddress: states.emailAddress,
      establishedOn: states.establishedOn,
      naics: data.naics,
      naicsDescription: data.naicsDescription,
      corporateTaxReferenceNumber: states.corporateTaxReferenceNumber,
    };

    const newObject = Object.entries(customerDetailsCorporation).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value)) {
          if (value.length === 1 && value[0] === "OUT") {
            return acc;
          }
        } else if (
          value !== undefined &&
          value !== null &&
          value !== "" &&
          value !== "OUT"
        ) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );
    dispatch(
      setForm({
        descriptionOfBusinessNature: data.descriptionOfBusinessNature,
      }),
    );
    dispatch(
      setForm({
        corporationCustomerData: newObject,
      }),
    );
    router.push("/kyc");
  };

  const handleNaics = (data: { value: string; code: string }) => {
    setValue("naicsDescription", data.value, { shouldValidate: true });
    setValue("naics", data.code, { shouldValidate: true });
    setFocus("descriptionOfBusinessNature");
    setNaics([]);
  };

  return (
    <ScrollView contentContainerClassName="flex-grow bg-white1">
      <View className="z-50 absolute top-0 right-0 m-8">
        <ButtonLogout />
      </View>
      <View className="items-center justify-center w-full h-full">
        <Logo className="m-2" />
        <View className="m-2">
          <Title text={t("onboarding/corporation/description:title")} />
        </View>
        <View className="items-center h-fit w-full lg:w-3/5 lg:h-152 lg:border lg:border-solid lg:border-gray lg:rounded-md lg:shadow">
          <Pressable
            onPress={() => {
              router.back();
            }}
            className="absolute z-[60] top-0 left-4 lg:left-6 lg:top-10"
          >
            <AntDesign name="arrowleft" size={24} color="#BDBDBD" />
          </Pressable>
          <View className="w-4/5 items-center mb-1 lg:w-full">
            <ProgressBarComponent progress={0} animatedValue={0.25} />
          </View>
          <View className="flex-grow justify-center w-full items-center z-50">
            <View className="z-50">
              <InputChoose
                name="corporateEntityType"
                label={t(
                  "onboarding/corporation/description:input.corporateEntityType",
                )}
                control={control}
                data={{ CorporationCustomerCorporateEntityType }}
                handleChange={(data) => setEntityType(data)}
              />
            </View>
            {entityType === "OTHER" && (
              <View className="z-40">
                <Input
                  name="corporateEntityTypeDescription"
                  label={t(
                    "onboarding/corporation/description:input.corporateEntityTypeDescription",
                  )}
                  control={control}
                />
              </View>
            )}
            <View className="z-30">
              <InputDropList
                name="naics"
                handleChange={(data) => {
                  handleNaics(data);
                }}
                clearValueFunction={() => {
                  setNaics([]);
                }}
                label={t("onboarding/corporation/description:input.naics")}
                onSearchChange={(term) => setSearchTerm(term)}
                data={naics}
                control={control}
              />
            </View>
            <View className="z-20">
              <Input
                name="descriptionOfBusinessNature"
                label={t(
                  "onboarding/corporation/description:input.descriptionOfBusinessNature",
                )}
                control={control}
              />
            </View>
          </View>
          <View className="w-full items-center">
            <Button
              label={t("common:button.nextstep")}
              onClick={handleSubmit(onSubmit)}
            />
          </View>
        </View>
        <View className="flex-grow justify-end">
          <Copyright />
        </View>
      </View>
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
